import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getToken } from '../services/api';
import './ContentSummarize.css';

// 풀대사 열닫 컴포넌트
const TranscriptSection = ({ transcript }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="result-section transcript-section">
      <button
        type="button"
        className={`transcript-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="transcript-toggle-left">
          <span className="section-icon">🎙️</span>
          <span className="transcript-toggle-title">풀 대사</span>
          <span className="transcript-length">
            {transcript.length.toLocaleString()}자
          </span>
        </span>
        <span className={`transcript-arrow ${isOpen ? 'open' : ''}`}>▾</span>
      </button>
      {isOpen && (
        <div className="transcript-body">
          <pre className="transcript-text">{transcript}</pre>
          <div className="transcript-actions">
            <button
              type="button"
              className="transcript-copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(transcript);
                alert('클립보드에 복사되었습니다.');
              }}
            >
              📋 복사
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const ContentSummarize = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState('url');

  // Input states
  const [textInput, setTextInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Analysis type (multi-select for YouTube)
  const [selectedTypes, setSelectedTypes] = useState(new Set(['organize']));
  const [isYoutubeUrl, setIsYoutubeUrl] = useState(false);

  // Result states
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Batch job states
  const [batchJobs, setBatchJobs] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [batchResults, setBatchResults] = useState([]);
  const [activeBatchTab, setActiveBatchTab] = useState(null);

  // History + Pagination
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const HISTORY_PER_PAGE = 10;

  // Fetch history on mount + restore active batch
  useEffect(() => {
    fetchHistory(1);
    // 진행 중이던 배치 작업 복원
    const savedBatchId = localStorage.getItem('ai_active_batch');
    if (savedBatchId) {
      setLoading(true);
      startPolling(savedBatchId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 페이지 변경 시 히스토리 다시 로드
  const handlePageChange = (page) => {
    setHistoryPage(page);
    fetchHistory(page);
  };
  const fetchHistory = async (page = 1) => {
    setIsLoadingHistory(true);
    try {
      const token = getToken();
      const offset = (page - 1) * HISTORY_PER_PAGE;
      const response = await fetch(
        `/api/ai/summarize/history?limit=${HISTORY_PER_PAGE}&offset=${offset}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setHistory(data.data || []);
          setHistoryTotal(data.total || 0);
        }
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // YouTube URL 감지
  const detectYoutubeUrl = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    ];
    return patterns.some(p => p.test(url));
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setUrlInput(url);
    const isYt = detectYoutubeUrl(url);
    setIsYoutubeUrl(isYt);
    if (isYt && selectedTypes.size === 0) {
      setSelectedTypes(new Set(['organize']));
    }
  };

  // Toggle analysis type in multi-select Set
  const toggleAnalysisType = (type) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        // Prevent deselecting the last one
        if (next.size <= 1) return prev;
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // Batch polling logic
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const pollBatchStatus = useCallback(async (batchId) => {
    try {
      const token = getToken();
      const response = await fetch(`/api/ai/summarize/batch/${batchId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) {
        setBatchJobs(data);
        const jobs = data.jobs || [];
        const allDone = jobs.every(j => j.status === 'completed' || j.status === 'failed');
        if (allDone) {
          stopPolling();
          setLoading(false);
          localStorage.removeItem('ai_active_batch');
          // Collect results from completed jobs
          const completedResults = jobs
            .filter(j => j.status === 'completed' && j.result)
            .map(j => ({ analysisType: j.analysisType, result: j.result }));
          setBatchResults(completedResults);
          if (completedResults.length > 0) {
            setActiveBatchTab(completedResults[0].analysisType);
          }
          setHistoryPage(1);
          fetchHistory(1);
        }
      }
    } catch (err) {
      console.error('Batch polling error:', err);
    }
  }, [stopPolling]);

  const startPolling = useCallback((batchId) => {
    stopPolling();
    pollingIntervalRef.current = setInterval(() => {
      pollBatchStatus(batchId);
    }, 3000);
    // Also poll immediately
    pollBatchStatus(batchId);
  }, [stopPolling, pollBatchStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    const allowedExtensions = ['.pdf', '.docx', '.txt'];
    const fileExt = '.' + selectedFile.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExt)) {
      setError(t('contentSummarize.validation.invalidFileType'));
      setFile(null);
      return;
    }

    // Validate file size (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError(t('contentSummarize.validation.fileTooLarge'));
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      handleFileChange({ target: { files: [droppedFile] } });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate input based on active tab
    if (activeTab === 'text' && !textInput.trim()) {
      setError(t('contentSummarize.validation.enterText'));
      return;
    }
    if (activeTab === 'url' && !urlInput.trim()) {
      setError(t('contentSummarize.validation.enterUrl'));
      return;
    }
    if (activeTab === 'file' && !file) {
      setError(t('contentSummarize.validation.selectFile'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setBatchJobs(null);
    setBatchResults([]);
    setActiveBatchTab(null);

    try {
      const token = getToken();

      // Determine if batch mode: YouTube URL + 2+ types selected
      const isBatchMode = activeTab === 'url' && isYoutubeUrl && selectedTypes.size >= 2;

      if (isBatchMode) {
        // Batch submission
        const payload = {
          inputType: 'url',
          url: urlInput,
          analysisTypes: Array.from(selectedTypes)
        };
        const response = await fetch('/api/ai/summarize/batch', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (response.ok && data.success) {
          setBatchJobs(data);
          localStorage.setItem('ai_active_batch', data.batchId);
          startPolling(data.batchId);
          // Don't clear inputs yet - user might want to see them
        } else {
          setError(data.error || t('contentSummarize.error'));
          setLoading(false);
        }
        return;
      }

      // Non-batch: existing synchronous flow
      let response;

      if (activeTab === 'file') {
        // File upload with FormData
        const formData = new FormData();
        formData.append('file', file);
        formData.append('inputType', 'file');
        response = await fetch('/api/ai/summarize', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      } else {
        // Text or URL
        const payload = {
          inputType: activeTab,
          text: activeTab === 'text' ? textInput : undefined,
          url: activeTab === 'url' ? urlInput : undefined,
        };
        // URL 탭에서는 analysisType 전송 (single type)
        if (activeTab === 'url') {
          const typesArr = Array.from(selectedTypes);
          payload.analysisType = typesArr[0] || 'summary';
        }
        response = await fetch('/api/ai/summarize', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setResult(data.data);
        setHistoryPage(1);
        fetchHistory(1); // Refresh history
        // Clear inputs
        setTextInput('');
        setUrlInput('');
        setFile(null);
      } else {
        setError(data.error || t('contentSummarize.error'));
      }
    } catch (err) {
      console.error('Summarization error:', err);
      setError(t('contentSummarize.error'));
    } finally {
      // For non-batch, set loading false. For batch, polling handles it.
      if (!(activeTab === 'url' && isYoutubeUrl && selectedTypes.size >= 2)) {
        setLoading(false);
      }
    }
  };

  const handleHistoryItemClick = (item) => {
    // Format history item to match result structure
    const resultData = {
      summary: item.summary,
      keywords: item.keywords,
      keyPoints: item.keyPoints
    };
    // organized sections 복원
    if (item.sections && item.sections.length > 0) {
      resultData.organized = { title: item.title || '', sections: item.sections };
    }
    setResult(resultData);
    setSelectedItem(item);
    // Scroll to results
    setTimeout(() => {
      document.querySelector('.results-container')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleDeleteItem = async (e, itemId) => {
    e.stopPropagation();
    if (!window.confirm(t('contentSummarize.confirm.delete'))) return;

    try {
      const token = getToken();
      const response = await fetch(`/api/ai/summarize/history/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        if (selectedItem && selectedItem.id === itemId) {
          setSelectedItem(null);
          setResult(null);
        }
        fetchHistory(historyPage);
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  const handleClear = () => {
    setTextInput('');
    setUrlInput('');
    setFile(null);
    setError(null);
    setBatchJobs(null);
    setBatchResults([]);
    setActiveBatchTab(null);
    localStorage.removeItem('ai_active_batch');
    stopPolling();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // 마크다운 내보내기
  const exportMarkdown = () => {
    if (!result?.organized) return;
    const org = result.organized;
    let md = `# ${org.title || '정리 결과'}\n\n`;
    if (result.videoMeta) {
      md += `> 채널: ${result.videoMeta.channel || '-'} | 길이: ${result.videoMeta.durationFormatted || '-'}\n\n`;
    }
    if (org.overview) md += `## 개요\n${org.overview}\n\n`;
    if (org.sections) {
      org.sections.forEach((s, i) => {
        md += `## ${s.heading}\n`;
        if (s.content) s.content.forEach(c => { md += `- ${c}\n`; });
        if (s.keyTakeaway) md += `\n> 💡 ${s.keyTakeaway}\n`;
        md += '\n';
      });
    }
    if (org.keyPoints) {
      md += `## 핵심 포인트\n`;
      org.keyPoints.forEach(p => { md += `- ${p}\n`; });
      md += '\n';
    }
    if (org.keywords) {
      md += `**키워드**: ${org.keywords.join(', ')}\n\n`;
    }
    if (org.references && org.references.length > 0) {
      md += `## 참고\n`;
      org.references.forEach(r => { md += `- ${r}\n`; });
    }
    navigator.clipboard.writeText(md).then(() => {
      alert('마크다운이 클립보드에 복사되었습니다.');
    });
  };

  const renderResult = () => {
    if (!result) return null;

    // YouTube 정리 결과 (youtube-organize)
    if (result.analysisType === 'youtube-organize' && result.organized) {
      const org = result.organized;
      return (
        <>
          {/* 영상 메타 정보 */}
          {result.videoMeta && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '10px 16px', background: '#f8fafc',
              borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#64748b'
            }}>
              <span>🎬</span>
              {org.videoType && <span style={{
                padding: '2px 8px', background: '#e0e7ff', color: '#4338ca',
                borderRadius: '4px', fontSize: '11px', fontWeight: '600'
              }}>{org.videoType}</span>}
              <span>{result.videoMeta.channel}</span>
              <span>·</span>
              <span>{result.videoMeta.durationFormatted}</span>
            </div>
          )}

          {/* 키워드 */}
          {org.keywords && org.keywords.length > 0 && (
            <section className="result-section keywords-section">
              <h3 className="section-title">
                <span className="section-icon">🏷️</span>
                키워드
              </h3>
              <div className="keywords-container">
                {org.keywords.map((keyword, index) => (
                  <span key={index} className="keyword-tag">{keyword}</span>
                ))}
              </div>
            </section>
          )}

          {/* 개요 */}
          {org.overview && (
            <section className="result-section summary-section">
              <h3 className="section-title">
                <span className="section-icon">📌</span>
                개요
              </h3>
              <div className="summary-card">
                <p className="summary-text">{org.overview}</p>
              </div>
            </section>
          )}

          {/* 구조화된 섹션 */}
          {org.sections && org.sections.length > 0 && (
            <section className="result-section organized-section">
              <h3 className="section-title">
                <span className="section-icon">📋</span>
                정리 내용
              </h3>
              <div className="organized-content">
                {org.sections.map((section, idx) => (
                  <div key={idx} className="organize-section">
                    <h4 className="section-heading">{section.heading}</h4>
                    {section.content && section.content.length > 0 && (
                      <ul className="section-content">
                        {section.content.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {section.keyTakeaway && (
                      <div style={{
                        marginTop: '8px', padding: '8px 12px',
                        background: '#FEF3C7', borderRadius: '6px',
                        borderLeft: '3px solid #F59E0B',
                        fontSize: '13px', color: '#92400E'
                      }}>
                        💡 {section.keyTakeaway}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 핵심 포인트 */}
          {org.keyPoints && org.keyPoints.length > 0 && (
            <section className="result-section keypoints-section">
              <h3 className="section-title">
                <span className="section-icon">🎯</span>
                핵심 포인트
              </h3>
              <ol className="key-points-list">
                {org.keyPoints.map((point, index) => (
                  <li key={index} className="key-point-item">{point}</li>
                ))}
              </ol>
            </section>
          )}

          {/* 참고 자료 */}
          {org.references && org.references.length > 0 && (
            <section className="result-section" style={{ marginTop: '16px' }}>
              <h3 className="section-title">
                <span className="section-icon">🔗</span>
                참고 자료
              </h3>
              <ul style={{ listStyle: 'disc', paddingLeft: '20px', fontSize: '14px', color: '#475569' }}>
                {org.references.map((ref, i) => (
                  <li key={i} style={{ marginBottom: '4px' }}>{ref}</li>
                ))}
              </ul>
            </section>
          )}

          {/* 내보내기 버튼 */}
          <div style={{
            display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={exportMarkdown}
              style={{
                padding: '8px 16px', borderRadius: '6px',
                border: '1px solid #d1d5db', background: '#fff',
                fontSize: '13px', cursor: 'pointer', color: '#374151',
                display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              📋 마크다운 복사
            </button>
          </div>
        </>
      );
    }

    // 기존 결과 렌더링 — 순서: 요약 → 키워드 → 핵심 → 상세 분석 → 풀대사(열닫)
    return (
      <>
        {/* 1. Summary — 핵심 요약 */}
        {result.summary && (
          <section className="result-section summary-section">
            <h3 className="section-title">
              <span className="section-icon">📝</span>
              {t('contentSummarize.result.coreAnalysis')}
            </h3>
            <div className="summary-card">
              <p className="summary-text">{result.summary}</p>
            </div>
          </section>
        )}

        {/* 2. Keywords */}
        {result.keywords && result.keywords.length > 0 && (
          <section className="result-section keywords-section">
            <div className="keywords-container">
              {result.keywords.map((keyword, index) => (
                <span key={index} className="keyword-tag">{keyword}</span>
              ))}
            </div>
          </section>
        )}

        {/* 3. Key Points — 핵심 포인트 */}
        {result.keyPoints && result.keyPoints.length > 0 && (
          <section className="result-section keypoints-section">
            <h3 className="section-title">
              <span className="section-icon">🎯</span>
              {t('contentSummarize.result.keyPoints')}
            </h3>
            <div className="keypoints-card">
              {result.keyPoints.map((point, index) => (
                <div key={index} className="keypoint-item">
                  <span className="keypoint-number">{index + 1}</span>
                  <p className="keypoint-text">{point}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 4. Organized — 상세 분석 */}
        {result.organized && (
          <section className="result-section organized-section">
            <h3 className="section-title">
              <span className="section-icon">📋</span>
              {t('contentSummarize.result.organized')}
            </h3>
            <div className="organized-content">
              {result.organized.sections?.map((section, idx) => (
                <div key={idx} className="organize-section">
                  <h4 className="section-heading">{section.heading}</h4>
                  {section.content && section.content.length > 0 && (
                    <ul className="section-content">
                      {section.content.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.keyTakeaway && (
                    <div className="section-takeaway">
                      <span className="takeaway-label">핵심</span>
                      <span className="takeaway-text">{section.keyTakeaway}</span>
                    </div>
                  )}
                  {section.quotes && section.quotes.length > 0 && (
                    <blockquote className="section-quote">
                      {section.quotes.join(' ')}
                    </blockquote>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 5. Transcript — 풀대사 (열닫구조) */}
        {result.transcript && (
          <TranscriptSection transcript={result.transcript} />
        )}
      </>
    );
  };

  return (
    <div className="content-summarize">
      <header className="page-header">
        <h1>{t('contentSummarize.title')}</h1>
        <p>{t('contentSummarize.subtitle')}</p>
      </header>

      <div className="summarize-container">
        {/* Input Form */}
        <form onSubmit={handleSubmit} className="summarize-form">
          {/* Tabs */}
          <div className="tabs">
            <button
              type="button"
              className={`tab-button ${activeTab === 'url' ? 'active' : ''}`}
              onClick={() => setActiveTab('url')}
            >
              🔗 URL / YouTube
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'text' ? 'active' : ''}`}
              onClick={() => setActiveTab('text')}
            >
              {t('contentSummarize.tabs.text')}
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'file' ? 'active' : ''}`}
              onClick={() => setActiveTab('file')}
            >
              {t('contentSummarize.tabs.file')}
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Text Input */}
            {activeTab === 'text' && (
              <div className="input-section">
                <textarea
                  className="text-input"
                  placeholder={t('contentSummarize.input.textPlaceholder')}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  disabled={loading}
                  rows={10}
                />
              </div>
            )}

            {/* URL Input */}
            {activeTab === 'url' && (
              <div className="input-section">
                <input
                  type="url"
                  className="url-input"
                  placeholder="YouTube URL 또는 웹페이지 URL을 입력하세요"
                  value={urlInput}
                  onChange={handleUrlChange}
                  disabled={loading}
                />

                {/* YouTube 감지 + 분석 타입 선택 */}
                {isYoutubeUrl && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '10px',
                      fontSize: '13px',
                      color: '#64748b'
                    }}>
                      <span>🎬</span>
                      <span>YouTube 영상이 감지되었습니다</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px',
                      fontSize: '12px',
                      color: '#94a3b8'
                    }}>
                      <span>복수 선택 가능</span>
                      <span style={{
                        display: 'inline-block',
                        width: '4px', height: '4px',
                        borderRadius: '50%',
                        background: '#cbd5e1'
                      }} />
                      <span>{selectedTypes.size}개 선택됨</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}>
                      {[
                        { value: 'transcript', label: '📝 대사 추출', desc: '무료' },
                        { value: 'summary', label: '📊 요약', desc: '핵심만' },
                        { value: 'organize', label: '📋 정리', desc: '추천', recommended: true }
                      ].map(opt => {
                        const isSelected = selectedTypes.has(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleAnalysisType(opt.value)}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '20px',
                              border: isSelected ? '2px solid #3B82F6' : '1px solid #d1d5db',
                              background: isSelected ? '#3B82F6' : '#fff',
                              color: isSelected ? '#ffffff' : '#374151',
                              fontSize: '13px',
                              fontWeight: isSelected ? '600' : '400',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {isSelected && <span style={{ fontSize: '12px' }}>✓</span>}
                            {opt.label}
                            <span style={{
                              fontSize: '11px',
                              color: isSelected ? 'rgba(255,255,255,0.8)' : (opt.recommended ? '#16a34a' : '#9ca3af'),
                              fontWeight: opt.recommended ? '600' : '400'
                            }}>
                              ({opt.desc})
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {selectedTypes.size >= 2 && (
                      <p style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#3B82F6',
                        lineHeight: '1.5',
                        background: '#EFF6FF',
                        padding: '6px 10px',
                        borderRadius: '6px'
                      }}>
                        💡 {selectedTypes.size}개 분석을 백그라운드에서 동시에 처리합니다. 다른 페이지를 보셔도 괜찮습니다.
                      </p>
                    )}
                    {selectedTypes.size === 1 && selectedTypes.has('organize') && (
                      <p style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#64748b',
                        lineHeight: '1.5'
                      }}>
                        💡 정리는 영상 내용을 구조화하여 재구성합니다. 요약과 달리 원본 내용의 70-90%를 보존합니다.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* File Input */}
            {activeTab === 'file' && (
              <div className="input-section">
                <div
                  className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <input
                    type="file"
                    id="fileInput"
                    className="file-input-hidden"
                    accept=".pdf,.docx,.txt"
                    onChange={handleFileChange}
                    disabled={loading}
                    style={{ display: 'none' }}
                  />
                  {file ? (
                    <div className="file-info">
                      <span className="file-icon">📄</span>
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  ) : (
                    <div className="drop-zone-content">
                      <span className="drop-icon">📁</span>
                      <p className="drop-text">{t('contentSummarize.input.fileDrop')}</p>
                      <p className="drop-hint">{t('contentSummarize.input.fileHint')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-message">
              <div className="loading-content">
                <span className="loading-spinner"></span>
                <div className="loading-text">
                  <p className="loading-title">{t('contentSummarize.loading')}</p>
                  <p className="loading-subtitle">{t('contentSummarize.loadingSubtext')}</p>
                </div>
              </div>
              <div className="loading-progress-bar">
                <div className="loading-progress-fill"></div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              className="clear-button"
              onClick={handleClear}
              disabled={loading}
            >
              {t('contentSummarize.button.clear')}
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? t('contentSummarize.loading') : t('contentSummarize.button.summarize')}
            </button>
          </div>
        </form>

        {/* Batch Job Progress Card */}
        {batchJobs && (
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                백그라운드 분석 진행 상황
              </h3>
              {batchJobs.jobs && (() => {
                const completed = batchJobs.jobs.filter(j => j.status === 'completed' || j.status === 'failed').length;
                const total = batchJobs.jobs.length;
                return (
                  <span style={{ fontSize: '13px', color: '#64748b' }}>
                    {completed}/{total} 완료
                  </span>
                );
              })()}
            </div>

            {/* Progress bar */}
            {batchJobs.jobs && (() => {
              const completed = batchJobs.jobs.filter(j => j.status === 'completed' || j.status === 'failed').length;
              const total = batchJobs.jobs.length;
              const pct = total > 0 ? (completed / total) * 100 : 0;
              return (
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: '#e2e8f0',
                  borderRadius: '3px',
                  marginBottom: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: pct === 100 ? '#22c55e' : '#3B82F6',
                    borderRadius: '3px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              );
            })()}

            {/* Individual job statuses */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(batchJobs.jobs || []).map((job, idx) => {
                const typeLabels = {
                  transcript: '📝 대사 추출',
                  summary: '📊 요약',
                  organize: '📋 정리'
                };
                const statusConfig = {
                  pending: { icon: '⏳', label: '대기중', color: '#94a3b8' },
                  processing: { icon: '🔄', label: '처리중', color: '#3B82F6', animate: true },
                  completed: { icon: '✅', label: '완료', color: '#22c55e' },
                  failed: { icon: '❌', label: '실패', color: '#ef4444' }
                };
                const sc = statusConfig[job.status] || statusConfig.pending;
                return (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    background: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #f1f5f9'
                  }}>
                    <span style={{ fontSize: '14px', color: '#334155' }}>
                      {typeLabels[job.analysisType] || job.analysisType}
                    </span>
                    <span style={{
                      fontSize: '13px',
                      color: sc.color,
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      animation: sc.animate ? 'batchPulse 1.5s ease-in-out infinite' : 'none'
                    }}>
                      <span>{sc.icon}</span>
                      <span>{sc.label}</span>
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Info text while processing */}
            {batchJobs.jobs && !batchJobs.jobs.every(j => j.status === 'completed' || j.status === 'failed') && (
              <p style={{
                marginTop: '12px',
                fontSize: '12px',
                color: '#64748b',
                textAlign: 'center',
                lineHeight: '1.5'
              }}>
                다른 페이지를 보셔도 괜찮습니다. 작업이 백그라운드에서 진행됩니다.
              </p>
            )}

            {/* Inline keyframe for pulse animation */}
            <style>{`
              @keyframes batchPulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
              }
            `}</style>
          </div>
        )}

        {/* Batch Results (tabbed) */}
        {batchResults.length > 0 && (
          <div className="results-container">
            <div className="results-header">
              <h2 className="results-title">분석 결과</h2>
            </div>

            {/* Tabs */}
            <div style={{
              display: 'flex',
              gap: '4px',
              marginBottom: '20px',
              borderBottom: '1px solid #e2e8f0',
              paddingBottom: '0'
            }}>
              {batchResults.map(br => {
                const tabLabels = {
                  transcript: '📝 대사',
                  summary: '📊 요약',
                  organize: '📋 정리'
                };
                const isActive = activeBatchTab === br.analysisType;
                return (
                  <button
                    key={br.analysisType}
                    type="button"
                    onClick={() => setActiveBatchTab(br.analysisType)}
                    style={{
                      padding: '10px 18px',
                      border: 'none',
                      borderBottom: isActive ? '2px solid #3B82F6' : '2px solid transparent',
                      background: 'transparent',
                      color: isActive ? '#3B82F6' : '#64748b',
                      fontSize: '14px',
                      fontWeight: isActive ? '600' : '400',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {tabLabels[br.analysisType] || br.analysisType}
                  </button>
                );
              })}
            </div>

            {/* Active tab content - reuse existing renderers */}
            {batchResults.map(br => {
              if (br.analysisType !== activeBatchTab) return null;
              const r = br.result;
              if (!r) return null;

              // Transcript result
              if (br.analysisType === 'transcript' && r.transcript) {
                return (
                  <div key={br.analysisType}>
                    <section className="result-section">
                      <h3 className="section-title">
                        <span className="section-icon">📝</span>
                        대사 추출 결과
                      </h3>
                      <div className="summary-card" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <p className="summary-text" style={{ whiteSpace: 'pre-wrap' }}>{r.transcript}</p>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(r.transcript);
                            alert('클립보드에 복사되었습니다.');
                          }}
                          style={{
                            padding: '6px 12px', borderRadius: '6px',
                            border: '1px solid #d1d5db', background: '#fff',
                            fontSize: '12px', cursor: 'pointer', color: '#374151'
                          }}
                        >
                          📋 복사
                        </button>
                      </div>
                    </section>
                  </div>
                );
              }

              // Summary result
              if (br.analysisType === 'summary') {
                return (
                  <div key={br.analysisType}>
                    {r.keywords && r.keywords.length > 0 && (
                      <section className="result-section keywords-section">
                        <h3 className="section-title">
                          <span className="section-icon">🏷️</span>
                          {t('contentSummarize.result.keywords')}
                        </h3>
                        <div className="keywords-container">
                          {r.keywords.map((keyword, index) => (
                            <span key={index} className="keyword-tag">{keyword}</span>
                          ))}
                        </div>
                      </section>
                    )}
                    {r.summary && (
                      <section className="result-section summary-section">
                        <h3 className="section-title">
                          <span className="section-icon">📝</span>
                          {t('contentSummarize.result.coreAnalysis')}
                        </h3>
                        <div className="summary-card">
                          <p className="summary-text">{r.summary}</p>
                        </div>
                      </section>
                    )}
                    {r.keyPoints && r.keyPoints.length > 0 && (
                      <section className="result-section keypoints-section">
                        <h3 className="section-title">
                          <span className="section-icon">🎯</span>
                          {t('contentSummarize.result.keyPoints')}
                        </h3>
                        <ol className="key-points-list">
                          {r.keyPoints.map((point, index) => (
                            <li key={index} className="key-point-item">{point}</li>
                          ))}
                        </ol>
                      </section>
                    )}
                  </div>
                );
              }

              // Organize result (youtube-organize)
              if (br.analysisType === 'organize' && r.organized) {
                const org = r.organized;
                return (
                  <div key={br.analysisType}>
                    {r.videoMeta && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '10px 16px', background: '#f8fafc',
                        borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#64748b'
                      }}>
                        <span>🎬</span>
                        {org.videoType && <span style={{
                          padding: '2px 8px', background: '#e0e7ff', color: '#4338ca',
                          borderRadius: '4px', fontSize: '11px', fontWeight: '600'
                        }}>{org.videoType}</span>}
                        <span>{r.videoMeta.channel}</span>
                        <span>·</span>
                        <span>{r.videoMeta.durationFormatted}</span>
                      </div>
                    )}
                    {org.keywords && org.keywords.length > 0 && (
                      <section className="result-section keywords-section">
                        <h3 className="section-title">
                          <span className="section-icon">🏷️</span>
                          키워드
                        </h3>
                        <div className="keywords-container">
                          {org.keywords.map((keyword, index) => (
                            <span key={index} className="keyword-tag">{keyword}</span>
                          ))}
                        </div>
                      </section>
                    )}
                    {org.overview && (
                      <section className="result-section summary-section">
                        <h3 className="section-title">
                          <span className="section-icon">📌</span>
                          개요
                        </h3>
                        <div className="summary-card">
                          <p className="summary-text">{org.overview}</p>
                        </div>
                      </section>
                    )}
                    {org.sections && org.sections.length > 0 && (
                      <section className="result-section organized-section">
                        <h3 className="section-title">
                          <span className="section-icon">📋</span>
                          정리 내용
                        </h3>
                        <div className="organized-content">
                          {org.sections.map((section, sIdx) => (
                            <div key={sIdx} className="organize-section">
                              <h4 className="section-heading">{section.heading}</h4>
                              {section.content && section.content.length > 0 && (
                                <ul className="section-content">
                                  {section.content.map((item, i) => (
                                    <li key={i}>{item}</li>
                                  ))}
                                </ul>
                              )}
                              {section.keyTakeaway && (
                                <div style={{
                                  marginTop: '8px', padding: '8px 12px',
                                  background: '#FEF3C7', borderRadius: '6px',
                                  borderLeft: '3px solid #F59E0B',
                                  fontSize: '13px', color: '#92400E'
                                }}>
                                  💡 {section.keyTakeaway}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </section>
                    )}
                    {org.keyPoints && org.keyPoints.length > 0 && (
                      <section className="result-section keypoints-section">
                        <h3 className="section-title">
                          <span className="section-icon">🎯</span>
                          핵심 포인트
                        </h3>
                        <ol className="key-points-list">
                          {org.keyPoints.map((point, index) => (
                            <li key={index} className="key-point-item">{point}</li>
                          ))}
                        </ol>
                      </section>
                    )}
                    {org.references && org.references.length > 0 && (
                      <section className="result-section" style={{ marginTop: '16px' }}>
                        <h3 className="section-title">
                          <span className="section-icon">🔗</span>
                          참고 자료
                        </h3>
                        <ul style={{ listStyle: 'disc', paddingLeft: '20px', fontSize: '14px', color: '#475569' }}>
                          {org.references.map((ref, i) => (
                            <li key={i} style={{ marginBottom: '4px' }}>{ref}</li>
                          ))}
                        </ul>
                      </section>
                    )}
                  </div>
                );
              }

              return null;
            })}
          </div>
        )}

        {/* Results (non-batch / single result) */}
        {result && (
          <div className="results-container">
            <div className="results-header">
              <h2 className="results-title">{t('contentSummarize.result.summary')}</h2>
              {result.provider && (
                <div className="result-metadata">
                  <span className="result-badge">
                    {result.provider === 'gemini' && '🔷 Gemini'}
                    {result.provider === 'gpt-mini' && '⚡ GPT-5 mini'}
                    {result.provider === 'groq' && '⚡ GPT-5 mini'}
                    {result.provider === 'anthropic' && '🧠 Claude'}
                    {result.provider === 'openai' && '🤖 GPT'}
                  </span>
                  {result.remaining !== undefined && result.remaining >= 0 && (
                    <span className="remaining-badge">
                      {t('contentSummarize.remaining')}: {result.remaining}/{result.limit}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Render results */}
            {renderResult()}
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <section className="history-section">
            <div className="history-header">
              <h2 className="history-title">{t('contentSummarize.history.title')}</h2>
            </div>

            {isLoadingHistory ? (
              <div className="history-loading">
                <span className="loading-spinner"></span>
                <span>{t('contentSummarize.history.loading')}</span>
              </div>
            ) : (
              <div className="board-container">
                <table className="board-table">
                  <thead>
                    <tr>
                      <th className="col-no">{t('contentSummarize.history.colNo')}</th>
                      <th className="col-type">{t('contentSummarize.history.colType')}</th>
                      <th className="col-title">{t('contentSummarize.history.colTitle')}</th>
                      <th className="col-date">{t('contentSummarize.history.colDate')}</th>
                      <th className="col-actions">{t('contentSummarize.history.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item, index) => (
                      <tr
                        key={item.id}
                        className={`board-row ${selectedItem?.id === item.id ? 'selected' : ''}`}
                        onClick={() => handleHistoryItemClick(item)}
                      >
                        <td className="col-no">{historyTotal - ((historyPage - 1) * HISTORY_PER_PAGE) - index}</td>
                        <td className="col-type">
                          <span className="type-badge">{item.type}</span>
                        </td>
                        <td className="col-title">
                          <div className="title-content">
                            {item.title && (
                              <span className="title-main">{item.title}</span>
                            )}
                            {item.sourceUrl && (
                              <a
                                href={item.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="title-url"
                                onClick={e => e.stopPropagation()}
                              >
                                {item.sourceUrl.length > 50
                                  ? item.sourceUrl.slice(0, 50) + '...'
                                  : item.sourceUrl}
                              </a>
                            )}
                            {!item.title && item.summary && (
                              <span className="title-preview">
                                {item.summary.slice(0, 80)}
                                {item.summary.length > 80 ? '...' : ''}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="col-date">{formatDate(item.createdAt)}</td>
                        <td className="col-actions" onClick={e => e.stopPropagation()}>
                          <div className="action-buttons">
                            <button
                              className="action-btn delete"
                              onClick={(e) => handleDeleteItem(e, item.id)}
                              data-tooltip={t('common.delete')}
                            >🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {historyTotal > HISTORY_PER_PAGE && (() => {
                  const totalPages = Math.ceil(historyTotal / HISTORY_PER_PAGE);
                  const maxVisible = 5;
                  let startPage = Math.max(1, historyPage - Math.floor(maxVisible / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) pages.push(i);

                  return (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        disabled={historyPage === 1}
                        onClick={() => handlePageChange(1)}
                      >«</button>
                      <button
                        className="pagination-btn"
                        disabled={historyPage === 1}
                        onClick={() => handlePageChange(historyPage - 1)}
                      >‹</button>
                      {startPage > 1 && <span className="pagination-dots">…</span>}
                      {pages.map(p => (
                        <button
                          key={p}
                          className={`pagination-btn ${p === historyPage ? 'active' : ''}`}
                          onClick={() => handlePageChange(p)}
                        >{p}</button>
                      ))}
                      {endPage < totalPages && <span className="pagination-dots">…</span>}
                      <button
                        className="pagination-btn"
                        disabled={historyPage === totalPages}
                        onClick={() => handlePageChange(historyPage + 1)}
                      >›</button>
                      <button
                        className="pagination-btn"
                        disabled={historyPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                      >»</button>
                    </div>
                  );
                })()}
              </div>
            )}
          </section>
        )}

        {/* Empty State */}
        {!loading && !result && history.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <p className="empty-title">{t('contentSummarize.history.empty')}</p>
            <p className="empty-description">{t('contentSummarize.emptyDescription')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentSummarize;
